import {
  SESSIONS_LIVE_STREAM_PATH,
  type SessionLiveEvent,
} from "../../../../../packages/shared-types/src/index.js";
import { getControlPlaneRuntimeConfig } from "@/app/runtime-config";
import { applyControlAuthHeaders } from "./client.js";

export async function openSessionLiveStream(params: {
  request: Record<string, unknown>;
  signal: AbortSignal;
  onEvent: (event: SessionLiveEvent) => void;
  path?: string;
}): Promise<void> {
  const runtimeConfig = getControlPlaneRuntimeConfig();
  const baseUrl = runtimeConfig.apiBaseUrl?.trim() || "";
  const headers = new Headers({
    Accept: "text/event-stream",
    "Content-Type": "application/json",
  });

  applyControlAuthHeaders(headers);

  const streamPath = params.path?.trim() || SESSIONS_LIVE_STREAM_PATH;

  const response = await fetch(`${baseUrl}${streamPath}`, {
    method: "POST",
    headers,
    credentials: "include",
    signal: params.signal,
    body: JSON.stringify(params.request),
  });
  if (!response.ok) {
    throw new Error(`Session live stream failed with status ${response.status}.`);
  }
  if (!response.body) {
    throw new Error("Session live stream response body is not available.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    buffer = processSseBuffer(buffer, params.onEvent);
  }
}

function processSseBuffer(buffer: string, onEvent: (event: SessionLiveEvent) => void): string {
  let remainder = buffer;
  while (true) {
    const boundary = remainder.indexOf("\n\n");
    if (boundary < 0) {
      return remainder;
    }
    const frame = remainder.slice(0, boundary);
    remainder = remainder.slice(boundary + 2);
    const event = parseSseFrame(frame);
    if (event) {
      onEvent(event);
    }
  }
}

function parseSseFrame(frame: string): SessionLiveEvent | null {
  const lines = frame
    .split("\n")
    .map((line) => line.trimEnd())
    .filter(Boolean);
  if (lines.length === 0 || lines.every((line) => line.startsWith(":"))) {
    return null;
  }
  const dataLine = lines.find((line) => line.startsWith("data:"));
  if (!dataLine) {
    return null;
  }
  try {
    return JSON.parse(dataLine.slice(5).trim()) as SessionLiveEvent;
  } catch {
    return null;
  }
}
